<template>
    <Slide :title="title" :subtitle="subtitle">
        <div>
            <div class="cr:flex cr:items-center cr:justify-center cr:py-9" v-if="loadingPrices">
                <LoadingSpinner :inline="true"/>
            </div>
            <div v-else>
                <p>Below are the available lead pricing options for your selected industry and states. Proceed to the next step to complete your registration and
                    start managing your campaigns and leads.</p>
                <div v-for="(statePrices, industry) in groupedPrices">
                    <div v-for="(prices, state) in statePrices" class="cr:mt-9">
                        <p class="cr:font-semibold cr:mb-1">{{ industry }} prices in {{ state }}</p>
                        <div class="cr:grid cr:grid-cols-3 cr:py-2 cr:text-gray-800 cr:text-sm cr:font-semibold" :class="[originClasses]">
                            <p class="cr:px-3">Name</p>
                            <p class="cr:px-3">Lead Price</p>
                            <p class="cr:px-3">Description</p>
                        </div>
                        <div v-for="saleType in saleTypes" class="cr:text-sm cr:bg-gray-50 cr:even:bg-gray-100 cr:border-b cr:border-gray-200">
                            <div v-if="getMinPrice(saleType.name, prices) && getMaxPrice(saleType.name, prices)" class="cr:grid cr:grid-cols-3 cr:items-center">
                                <p class="cr:px-3 cr:py-5">{{ saleType.name }}</p>
                                <p class="cr:px-3 cr:py-5">${{ getMinPrice(saleType.name, prices) }} - ${{ getMaxPrice(saleType.name, prices) }}</p>
                                <p class="cr:px-3 cr:py-5">{{ saleType.description }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Slide>
</template>

<script setup lang="ts">

import Slide from "@/components/slides/Slide.vue";
import {computed, onMounted, type Ref, ref} from "vue";
import {useRegistrationStore} from "@/stores/registration.ts";
import {useErrorStore} from "@/stores/error.ts";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import {type LeadPrice, type IndustryLeadPrice} from "@/interfaces/IndustryLeadPrice.ts";
import {ORIGINS} from "@/services/ConstantSerice.ts";

const props = defineProps({
    title: {
        type: String,
        default: 'Lead Prices'
    },
    subtitle: {
        type: String,
        default: ''
    }
});

const registrationStore = useRegistrationStore();
const errorStore = useErrorStore();
const leadPrices: Ref<LeadPrice[]> = ref([]);
const loadingPrices = ref(false);
const groupedPrices: Ref<IndustryLeadPrice> = ref({});
const saleTypes = [
    {name: 'Exclusive', description: 'Matched only to you'},
    {name: 'Duo', description: 'Matched with 2 companies'},
    {name: 'Trio', description: 'Matched with 3 companies'},
    {name: 'Quad', description: 'Matched with 4 companies'},
    {name: 'Unverified', description: 'Phone number not verified'},
    // {name: 'Email Only', description: 'Communication by email only'},
]

const canContinue = computed(() => {
    return true;
});

const slidePayload = computed(() => {
    return {}
});

defineExpose({
    canContinue,
    slidePayload
});

const emit = defineEmits(['next-slide']);

onMounted(() => initializePrices())

const initializePrices = async () => {
    if (!registrationStore.registeringCompany.reference) {
        return;
    }

    loadingPrices.value = true;

    await registrationStore.apiService.getLeadPrices(registrationStore.registeringCompany.reference)
        .then(resp => {
            leadPrices.value = resp.data.data.prices;
            if (!leadPrices.value.length) {
                emit('next-slide');
                return;
            }

            groupedPrices.value = leadPrices.value.reduce((acc: any, item: any) => {
                if (!acc[item.industry]) {
                    acc[item.industry] = {};
                }

                if (!acc[item.industry][item.state]) {
                    acc[item.industry][item.state] = [];
                }

                acc[item.industry][item.state].push(item);

                return acc;
            }, {});
        })
        .catch(e => {
            console.error(e);
            errorStore.report('Something went wrong. Please try again.')
        })

    loadingPrices.value = false;
}

const getMinPrice = (saleType: string, prices: LeadPrice[]) => {
    return prices.find(price => price.sale_type.toLowerCase() === saleType.toLowerCase())?.min_price ?? null;
}
const getMaxPrice = (saleType: string, prices: LeadPrice[]) => {
    return prices.find(price => price.sale_type.toLowerCase() === saleType.toLowerCase())?.max_price ?? null;
}

const originClasses = computed(() => {
    switch (registrationStore.registrationOrigin) {
        case ORIGINS.BE:
            return 'cr:bg-[var(--be-primary-light)]';
        case ORIGINS.KE:
            return 'cr:bg-[var(--ke-primary-light)]';
        case ORIGINS.FIXR:
            return 'cr:bg-[var(--fixr-primary-light)]';
        case ORIGINS.SR:
            return 'cr:bg-[var(--sr-primary-light)]';
        case ORIGINS.RC:
            return 'cr:bg-[var(--rc-primary-light)]';
        default:
            return 'cr:bg-[var(--default-primary-light)]';
    }
});

</script>

